import type { ContentModerationInfoItem, PostSearchItem } from '@ps/api/types';
import { ModerationStatusEnumItem } from '@ps/api/types';
import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import { formatMoney } from '@ps/functions/utils/currency';
import { sanitizeHtml } from '@ps/functions/utils/sanitize-html';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import { ModerationStatus } from '@ps/models/lib/moderation';

import { restrictedMedias } from '../../functions/restricted-media';
import type {
  PostContentKind,
  PostGatingViewModel,
  PostPayGate,
  PostTipGate,
  PostViewModel,
} from '../types';

/**
 * The slim post fields the {@link PostView} reads — a `Pick` (so the raw
 * `PostSearchItem` from the editor stays assignable) with `permaLink` replaced by
 * the url-code-FREE `permalinkPath` (`post/<year>/<month>/<slug>`). The visitor
 * cache stores only this; the full permalink is rebuilt at render from the live
 * urlCode.
 */
export type PostViewInput = Pick<
  PostSearchItem,
  | 'id'
  | 'postType'
  | 'title'
  | 'description'
  | 'textContent'
  | 'medias'
  | 'unrestrictedLeadingMediaCount'
  | 'restricted'
  | 'payToView'
  | 'payToViewOnlyMode'
  | 'subscribeToView'
  | 'crowdTippingToView'
  | 'visibility'
  | 'liked'
  | 'likeCount'
  | 'commentCount'
  | 'allowComments'
  | 'publishedAt'
  | 'createdAt'
  | 'moderation'
> & {
  /** Post permalink path AFTER the url code (`post/<year>/<month>/<slug>`). */
  permalinkPath?: string;
};

/**
 * Map the generated `ModerationStatusEnumItem` (API boundary) onto the shared
 * `ModerationStatus` model enum. Exhaustive so a new backend status is a compile
 * error here rather than a silent `undefined` downstream.
 */
const MODERATION_STATUS_BY_ITEM: Record<
  ModerationStatusEnumItem,
  ModerationStatus
> = {
  [ModerationStatusEnumItem.Pending]: ModerationStatus.Pending,
  [ModerationStatusEnumItem.Accepted]: ModerationStatus.Accepted,
  [ModerationStatusEnumItem.Rejected]: ModerationStatus.Rejected,
  [ModerationStatusEnumItem.ReviewRequired]: ModerationStatus.ReviewRequired,
};

/** Read a post's moderation status as the model enum; `undefined` when unset. */
function moderationStatusOf(
  moderation: ContentModerationInfoItem | undefined,
): ModerationStatus | undefined {
  const status = moderation?.moderationStatus;
  return status ? MODERATION_STATUS_BY_ITEM[status] : undefined;
}

/** Author display info — resolved from the bio profile (the post row omits it). */
export interface PostViewAuthor {
  storeFrontName?: string;
  urlCode: string;
  avatar?: string;
}

/** Pay-to-view has no currency field of its own — prices are USD (backend rule). */
const PAY_CURRENCY = 'USD';

/** Drop the leading url-code segment from a server `permaLink`. */
function permalinkPathOf(permaLink: string | undefined): string | undefined {
  if (!permaLink) {
    return undefined;
  }
  const segments = permaLink.split('/');
  return segments.length > 1 ? segments.slice(1).join('/') : undefined;
}

/**
 * Project a raw `PostSearchItem` to the slim {@link PostViewInput}. Drops every
 * field the view doesn't render and strips the url code out of the permalink, so
 * what's cached is slim AND carries no urlCode. Used by the visitor loader (before
 * caching) and the editor (raw posts fetched client-side); both build VMs at render.
 */
export function toPostViewInput(post: PostSearchItem): PostViewInput {
  return {
    id: post.id,
    postType: post.postType,
    title: post.title,
    description: post.description,
    textContent: post.textContent,
    medias: post.medias,
    unrestrictedLeadingMediaCount: post.unrestrictedLeadingMediaCount,
    restricted: post.restricted,
    payToView: post.payToView,
    payToViewOnlyMode: post.payToViewOnlyMode,
    subscribeToView: post.subscribeToView,
    crowdTippingToView: post.crowdTippingToView,
    visibility: post.visibility,
    liked: post.liked,
    likeCount: post.likeCount,
    commentCount: post.commentCount,
    allowComments: post.allowComments,
    publishedAt: post.publishedAt,
    createdAt: post.createdAt,
    moderation: post.moderation,
    permalinkPath: permalinkPathOf(post.permaLink),
  };
}

/** A `MEDIA_GALLERY` post renders the carousel; anything else renders rich text. */
function contentKindOf(post: PostViewInput): PostContentKind {
  return post.postType === 'MEDIA_GALLERY' ? 'media' : 'text';
}

function payGateOf(pay: NonNullable<PostViewInput['payToView']>): PostPayGate {
  const price = pay.price ?? 0;
  const discounted = pay.discountEnabled === true && pay.discountPrice != null;
  if (discounted) {
    return {
      price: formatMoney(pay.discountPrice, PAY_CURRENCY),
      originalPrice: formatMoney(price, PAY_CURRENCY),
    };
  }
  return { price: formatMoney(price, PAY_CURRENCY) };
}

function tipGateOf(
  tip: NonNullable<PostViewInput['crowdTippingToView']>,
): PostTipGate {
  const target = tip.targetAmount || 0;
  const current = tip.currentAmount || 0;
  const progress = target > 0 ? Math.min(1, Math.max(0, current / target)) : 0;
  return {
    goalLabel: formatMoney(target, tip.currency),
    // Clamp the raised amount to the goal — 1:1 with legacy
    // `formatGroupTippingProgressLabel`.
    raisedLabel: `${formatMoney(
      Math.min(current, target),
      tip.currency,
    )} of ${formatMoney(target, tip.currency)} raised`,
    progress,
  };
}

/** Milliseconds in a day — for the pay-to-view-only cutoff. */
const MS_PER_DAY = 86_400_000;

/** The `*ToView` gate config + free-media count — the only fields gating reads. */
export type PostGatingInput = Pick<
  PostSearchItem,
  | 'subscribeToView'
  | 'payToView'
  | 'payToViewOnlyMode'
  | 'crowdTippingToView'
  | 'unrestrictedLeadingMediaCount'
  | 'publishedAt'
> & {
  // Widened to optional (required on `PostSearchItem`) so the chat-bubble card's
  // partial input still satisfies the gating input; only used as the pay-only
  // fallback date when `publishedAt` is absent.
  createdAt?: string;
};

/**
 * Epoch (ms) at/after which the post is PAY-ONLY. With pay-to-view-only mode on,
 * a post flips to purchase-only `daysAfterPublished` days after it goes live, so
 * a subscription no longer unlocks it. `undefined` when the mode is off. Pure —
 * it computes the cutoff INSTANT from the post's own publish date; whether "now"
 * is past it is decided later, on the client (no server clock here).
 */
function payToViewOnlyFromOf(post: PostGatingInput): number | undefined {
  const mode = post.payToViewOnlyMode;
  if (mode?.enabled !== true) {
    return undefined;
  }
  const publishedAt = post.publishedAt ?? post.createdAt;
  if (!publishedAt) {
    return undefined;
  }
  return (
    new Date(publishedAt).getTime() +
    (mode.daysAfterPublished ?? 0) * MS_PER_DAY
  );
}

/**
 * The post's monetization gates — subscribe / pay / tip can co-exist. Built from
 * the raw `*ToView` config, so it's available whether or not the current viewer
 * is restricted (the header chip describes the gate even when unlocked). Exported
 * so the chat-bubble card (`PostChatMessageView`) builds the SAME gating VM the
 * full `PostView` does.
 */
export function buildPostGating(post: PostGatingInput): PostGatingViewModel {
  return {
    subscribe: post.subscribeToView?.enabled
      ? {
          level: post.subscribeToView
            .level as unknown as PopStoreExtendedSubscriptionLevel,
        }
      : undefined,
    pay: post.payToView?.enabled ? payGateOf(post.payToView) : undefined,
    tip: post.crowdTippingToView?.enabled
      ? tipGateOf(post.crowdTippingToView)
      : undefined,
    openCount: post.unrestrictedLeadingMediaCount ?? 0,
    payToViewOnlyFrom: payToViewOnlyFromOf(post),
  };
}

function postViewModel(
  post: PostViewInput,
  author: PostViewAuthor,
  bioContext: BioContext | null,
): PostViewModel {
  const contentKind = contentKindOf(post);
  const publishedAt = post.publishedAt ?? post.createdAt;
  // Creator-authored HTML. The TipTap editor shapes it client-side only, so it
  // constrains nothing about what reaches the API — sanitize at the VM rather
  // than at the two `dangerouslySetInnerHTML` sinks reading this field, so rows
  // stored before the write-side guard render inert too.
  const bodyHtml =
    contentKind === 'text' && post.textContent != null
      ? sanitizeHtml(post.textContent)
      : undefined;
  // No bio context (the non-1:1 DM inbox, where posts from different creators
  // mix and there's no single creator scope) → no bio-scoped URL can be built,
  // so the permalink / share link are simply absent (both fields are optional).
  const permalinkPath = bioContext != null ? post.permalinkPath : undefined;

  return {
    id: post.id,
    permalinkHref:
      bioContext != null && permalinkPath
        ? bioUrl(bioContext).postPermalink(permalinkPath)
        : undefined,
    shareUrl:
      bioContext != null && permalinkPath
        ? bioUrl(bioContext, { share: true }).postPermalink(permalinkPath)
        : undefined,
    author: {
      name: author.storeFrontName ?? author.urlCode,
      handle: author.urlCode,
      avatarUrl: author.avatar,
    },
    title: post.title || undefined,
    // Full source caption; the view truncates it at render when not `full`.
    description: post.description?.trim() ?? '',
    bodyHtml,
    contentKind,
    medias: restrictedMedias(post),
    unrestrictedLeadingMediaCount: post.unrestrictedLeadingMediaCount,
    restricted: post.restricted === true,
    gating: buildPostGating(post),
    moderationStatus: moderationStatusOf(post.moderation),
    // API visibility is uppercase (`PRIVATE`); compare case-insensitively so the
    // owner's hide/unhide eye reflects the real state (was always false before).
    isPrivate:
      (post.visibility as string | undefined)?.toUpperCase() === 'PRIVATE',
    liked: post.liked ?? false,
    likeCount: post.likeCount ?? 0,
    commentCount: post.commentCount ?? 0,
    commentsEnabled: post.allowComments || (post.commentCount ?? 0) > 0,
    // Primitive only — the label is clock-relative and derives client-side.
    publishedAt: publishedAt ? new Date(publishedAt).getTime() : undefined,
  };
}

/**
 * Pure adapter: slim {@link PostViewInput}`[]` → {@link PostViewModel}`[]`. The
 * single place the post transport is read for the {@link PostView}; the pure views
 * consume only the resulting view models. Author + permalink are applied here at
 * render (from `author.urlCode`), so neither is baked into the cached input.
 *
 * The view models carry the FULL source text (caption + body) — truncation is a
 * render decision the {@link PostView} makes via its `full` prop (feed clips,
 * detail shows everything), never baked in here.
 */
export function postViewModels(params: {
  posts: PostViewInput[];
  author: PostViewAuthor;
  /** `null` on the non-1:1 DM inbox → permalink / share are left unbuilt. */
  bioContext: BioContext | null;
}): PostViewModel[] {
  return params.posts.map(post =>
    postViewModel(post, params.author, params.bioContext),
  );
}
